/**
 * The icon vocabulary, shared by the office app (rendered via
 * `@phosphor-icons/react`) and the marketing site (rendered by inlining
 * `@phosphor-icons/core` SVG assets at build time). One map, two renderers, so
 * a name can never mean two different glyphs.
 *
 * Keys are our semantic names, held stable from the two previous hand-drawn
 * sets so the migration is a value change rather than a rename sweep. Values are
 * Phosphor icon names in kebab-case, which double as the core asset file stems
 * (`assets/regular/<name>.svg`) and, PascalCased, the React component stems
 * (`dist/csr/<Name>` exporting `<Name>Icon`).
 */
export const ICON_CATALOG = {
  // Navigation and chrome
  gauge: "gauge",
  users: "users",
  landmark: "bank",
  "list-checks": "list-checks",
  timer: "timer",
  "credit-card": "credit-card",
  "file-text": "file-text",
  "calendar-check": "calendar-check",
  "trending-up": "trend-up",
  bell: "bell",
  search: "magnifying-glass",
  plus: "plus",
  check: "check",
  lock: "lock",
  clock: "clock",
  settings: "gear",
  "log-out": "sign-out",
  menu: "list",
  upload: "upload-simple",
  "rotate-ccw": "arrow-counter-clockwise",
  archive: "archive",
  trash: "trash",

  // Marketing surfaces and secondary UI
  building: "buildings",
  sparkles: "sparkle",
  shield: "shield",
  "shield-check": "shield-check",
  "alert-triangle": "warning",
  "arrow-left": "arrow-left",
  "arrow-right": "arrow-right",
  table: "table",
  activity: "pulse",
  "file-spreadsheet": "file-xls",
  map: "map-trifold",
  "pen-line": "pencil-simple-line",
  eye: "eye",
  "user-cog": "user-gear",

  // Theme switch
  sun: "sun",
  moon: "moon",
  desktop: "desktop",

  // Rail collapse toggle
  "sidebar-simple": "sidebar-simple",
  "caret-left": "caret-left",
  "caret-right": "caret-right",
} as const;

/** Our semantic icon names. The single vocabulary both frontends draw from. */
export type IconName = keyof typeof ICON_CATALOG;

/** The Phosphor names our catalog resolves to. */
export type PhosphorName = (typeof ICON_CATALOG)[IconName];

/** Every semantic name, for gallery listings and catalog audits. */
export const ICON_NAMES = Object.keys(ICON_CATALOG) as IconName[];

/**
 * Phosphor's six weights. `regular` is the default; `bold` for emphasis and
 * small sizes; `fill` as an active/selected state channel; `duotone` reserved
 * for marketing surfaces.
 */
export type IconWeight = "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
